import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ToastStatus } from './Toast';

interface ToastData {
  id: string;
  status: ToastStatus;
  message: string;
  /** Extra plain-text lines under the message — see Toast's `details`. */
  details?: string[];
  cta?: {
    text: string;
    onClick: () => void;
  };
  duration?: number;
  isExiting?: boolean;
}

interface ToastContextType {
  showToast: (options: Omit<ToastData, 'id'>) => void;
  hideToast: (id: string) => void;
  toasts: ToastData[];
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const showToast = useCallback((options: Omit<ToastData, 'id'>) => {
    const id = Date.now().toString();
    const newToast: ToastData = {
      id,
      ...options,
      duration: options.duration || 5000,
    };

    setToasts((prev) => [...prev, newToast]);

    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        hideToast(id);
      }, newToast.duration);
    }
  }, []);

  const hideToast = useCallback((id: string) => {
    // First mark as exiting to trigger exit animation
    setToasts((prev) => 
      prev.map((toast) => 
        toast.id === id ? { ...toast, isExiting: true } : toast
      )
    );
    
    // Then remove after animation duration
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 300); // Match animation duration
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, hideToast, toasts }}>
      {children}
    </ToastContext.Provider>
  );
};